import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Trash2, Pencil, UploadCloud, Instagram, Eye, Play, Film, Sparkles,
  Pin, PinOff, ArrowUpRight, RefreshCw, Image as ImageIcon
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIE = [
  "auto", "casa", "vita", "salute", "famiglia", "professioni",
  "aziende", "viaggio", "sinistri", "risparmio", "normativa", "mindset",
];
const TIPI = ["reel", "pillola", "carosello", "post"];

const TIPO_ICON = { reel: Film, pillola: Play, carosello: Sparkles, post: Instagram };

const EMPTY = {
  tipo: "reel",
  categoria: "mindset",
  titolo: "",
  descrizione: "",
  cta_testo: "",
  cta_link: "",
  link_instagram: "",
  cover_file_id: "",
  durata_sec: 0,
  tag: [],
  pinned: false,
  ordine: 100,
  attivo: true,
};

export default function RivoluzioneAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rivoluzione/contenuti`, { withCredentials: true });
      setItems(data || []);
    } catch {
      toast.error("Errore caricamento contenuti");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing({ ...EMPTY }); setTagInput(""); setEditOpen(true); };
  const openEdit = (it) => { setEditing({ ...it, tag: it.tag || [] }); setTagInput(""); setEditOpen(true); };

  const save = async () => {
    if (!editing.titolo || editing.titolo.length < 3) {
      toast.error("Titolo obbligatorio (min 3 caratteri)"); return;
    }
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/rivoluzione/contenuto/${editing.id}`, editing, { withCredentials: true });
        toast.success("Contenuto aggiornato");
      } else {
        await axios.post(`${API}/admin/rivoluzione/contenuto`, editing, { withCredentials: true });
        toast.success("Contenuto creato");
      }
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const del = async (id) => {
    if (!confirm("Eliminare definitivamente questo contenuto?")) return;
    try {
      await axios.delete(`${API}/admin/rivoluzione/contenuto/${id}`, { withCredentials: true });
      toast.success("Eliminato");
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const togglePinned = async (it) => {
    try {
      await axios.patch(`${API}/admin/rivoluzione/contenuto/${it.id}`,
        { pinned: !it.pinned }, { withCredentials: true });
      load();
    } catch { toast.error("Errore"); }
  };

  const toggleAttivo = async (it) => {
    try {
      await axios.patch(`${API}/admin/rivoluzione/contenuto/${it.id}`,
        { attivo: !it.attivo }, { withCredentials: true });
      load();
    } catch { toast.error("Errore"); }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/rivoluzione/upload-cover`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      setEditing(s => ({ ...s, cover_file_id: data.file_id }));
      toast.success("Cover caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    }
    setUploading(false);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || editing.tag.includes(t) || editing.tag.length >= 8) { setTagInput(""); return; }
    setEditing(s => ({ ...s, tag: [...s.tag, t] }));
    setTagInput("");
  };

  const coverUrl = (fid) => fid ? `${API}/rivoluzione/cover/${fid}` : null;

  return (
    <div className="space-y-6" data-testid="rivoluzione-admin-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] rounded-2xl p-6 text-white">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Instagram className="w-5 h-5 text-[#D97B58]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97B58]">Canale Redazione</p>
          </div>
          <h2 className="font-heading text-2xl font-bold mb-1">Video-pillole @colucci_assicuratori</h2>
          <p className="text-sm text-white/60">News assicurative trasformate in reel da 60 secondi. Christian e' il volto della redazione, ma i contenuti sono dell'agenzia. Stile editoriale (Geopop).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="riv-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={openNew} className="bg-[#D97B58] hover:bg-[#c86b4a] text-white" data-testid="riv-new-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo contenuto
          </Button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-zinc-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-zinc-200">
          <Film className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500 mb-4">Nessun contenuto ancora. Crea il primo!</p>
          <Button onClick={openNew} className="bg-[#0F1F14] text-white hover:bg-[#1d4a2e]">
            <Plus className="w-4 h-4 mr-2" /> Crea contenuto
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="riv-items-grid">
          {items.map(it => {
            const Icon = TIPO_ICON[it.tipo] || Film;
            const cUrl = coverUrl(it.cover_file_id);
            return (
              <div key={it.id} className={`group relative rounded-2xl overflow-hidden border bg-white ${!it.attivo ? "opacity-50" : ""} ${it.pinned ? "border-[#D97B58] shadow-md" : "border-zinc-200"}`} data-testid={`riv-item-${it.id}`}>
                <div className="aspect-[4/5] bg-zinc-900 relative">
                  {cUrl ? (
                    <img src={cUrl} alt={it.titolo} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0F1F14] via-[#1d4a2e] to-[#D97B58] flex items-center justify-center">
                      <Icon className="w-10 h-10 text-white/40" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-bold uppercase tracking-wider text-zinc-900">
                    <Icon className="w-3 h-3" /> {it.tipo}
                  </div>
                  {it.pinned && (
                    <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full bg-[#D97B58] text-white text-[9px] font-bold uppercase">Top</div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-[9px] uppercase tracking-wider text-white/70 font-semibold mb-0.5">{it.categoria}</p>
                    <h3 className="text-xs sm:text-sm font-bold text-white leading-tight line-clamp-2">{it.titolo}</h3>
                    {typeof it.views === "number" && (
                      <div className="flex items-center gap-1 mt-1 text-[10px] text-white/60"><Eye className="w-3 h-3" /> {it.views}</div>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="p-2 flex items-center gap-1 bg-zinc-50 border-t border-zinc-100">
                  <Button variant="ghost" size="sm" className="h-7 flex-1 text-xs" onClick={() => openEdit(it)} data-testid={`riv-edit-${it.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Modifica
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => togglePinned(it)} title={it.pinned ? "Rimuovi pin" : "Metti in evidenza"}>
                    {it.pinned ? <PinOff className="w-3.5 h-3.5 text-[#D97B58]" /> : <Pin className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleAttivo(it)} title={it.attivo ? "Disattiva" : "Attiva"}>
                    <div className={`w-5 h-3 rounded-full transition-colors ${it.attivo ? "bg-[#0F1F14]" : "bg-zinc-300"} relative`}>
                      <div className={`absolute top-0.5 w-2 h-2 rounded-full bg-white transition-all ${it.attivo ? "left-2.5" : "left-0.5"}`} />
                    </div>
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => del(it.id)} title="Elimina">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog EDIT */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="riv-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing.id ? "Modifica contenuto" : "Nuovo contenuto"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Titolo *</Label>
              <Input value={editing.titolo} onChange={e => setEditing(s => ({ ...s, titolo: e.target.value }))}
                placeholder="Es. Kasko o Mini-Kasko? La scelta giusta in 60 secondi" data-testid="riv-input-titolo" />
            </div>

            <div>
              <Label>Tipo</Label>
              <Select value={editing.tipo} onValueChange={v => setEditing(s => ({ ...s, tipo: v }))}>
                <SelectTrigger data-testid="riv-select-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label>Categoria</Label>
              <Select value={editing.categoria} onValueChange={v => setEditing(s => ({ ...s, categoria: v }))}>
                <SelectTrigger data-testid="riv-select-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIE.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label>Descrizione breve</Label>
              <Textarea rows={3} value={editing.descrizione} onChange={e => setEditing(s => ({ ...s, descrizione: e.target.value }))}
                placeholder="Cosa imparera' chi guarda questo contenuto?" />
            </div>

            <div className="md:col-span-2">
              <Label>Link al post/reel Instagram</Label>
              <Input value={editing.link_instagram} onChange={e => setEditing(s => ({ ...s, link_instagram: e.target.value }))}
                placeholder="https://www.instagram.com/reel/..." data-testid="riv-input-link-ig" />
              <p className="text-xs text-zinc-400 mt-1">Lasciando vuoto, il click porta al profilo principale @colucci_assicuratori</p>
            </div>

            <div>
              <Label>CTA testo (opzionale)</Label>
              <Input value={editing.cta_testo} onChange={e => setEditing(s => ({ ...s, cta_testo: e.target.value }))}
                placeholder="Es. Calcola il tuo preventivo" />
            </div>

            <div>
              <Label>CTA link interno (opzionale)</Label>
              <Input value={editing.cta_link} onChange={e => setEditing(s => ({ ...s, cta_link: e.target.value }))}
                placeholder="Es. /questionario/auto" />
            </div>

            <div>
              <Label>Durata (secondi)</Label>
              <Input type="number" min={0} max={600} value={editing.durata_sec}
                onChange={e => setEditing(s => ({ ...s, durata_sec: parseInt(e.target.value) || 0 }))} />
            </div>

            <div>
              <Label>Ordine (basso = priorita')</Label>
              <Input type="number" value={editing.ordine}
                onChange={e => setEditing(s => ({ ...s, ordine: parseInt(e.target.value) || 100 }))} />
            </div>

            <div className="md:col-span-2">
              <Label>Tag (max 8)</Label>
              <div className="flex gap-2 mb-2">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Invio per aggiungere" />
                <Button type="button" onClick={addTag} variant="outline">Aggiungi</Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {editing.tag.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 text-xs text-zinc-700">
                    #{t}
                    <button onClick={() => setEditing(s => ({ ...s, tag: s.tag.filter(x => x !== t) }))} className="hover:text-red-600">×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Cover */}
            <div className="md:col-span-2">
              <Label>Cover / Thumbnail</Label>
              <div className="flex items-start gap-4">
                <div className="w-28 h-36 rounded-xl overflow-hidden bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                  {editing.cover_file_id ? (
                    <img src={coverUrl(editing.cover_file_id)} alt="cover" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-zinc-300" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 cursor-pointer text-sm font-medium">
                    <UploadCloud className="w-4 h-4" />
                    {uploading ? "Caricamento..." : (editing.cover_file_id ? "Sostituisci cover" : "Carica cover")}
                    <input type="file" accept="image/jpeg,image/png" onChange={uploadCover} className="hidden" disabled={uploading} data-testid="riv-upload-cover" />
                  </label>
                  <p className="text-xs text-zinc-400 mt-2">JPG o PNG, max 10MB. Rapporto consigliato 4:5 (formato reel/post).</p>
                  {editing.cover_file_id && (
                    <button onClick={() => setEditing(s => ({ ...s, cover_file_id: "" }))} className="text-xs text-red-600 hover:underline mt-1">
                      Rimuovi cover
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Switches */}
            <div className="md:col-span-2 flex flex-wrap gap-6 pt-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={editing.pinned} onCheckedChange={v => setEditing(s => ({ ...s, pinned: v }))} />
                <span className="font-medium">In evidenza (pinned)</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={editing.attivo} onCheckedChange={v => setEditing(s => ({ ...s, attivo: v }))} />
                <span className="font-medium">Attivo sul sito</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={save} className="flex-1 bg-[#0F1F14] hover:bg-[#1d4a2e] text-white" data-testid="riv-save-btn">
              Salva
            </Button>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            {editing.link_instagram && (
              <a href={editing.link_instagram} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-lg border border-zinc-300 hover:bg-zinc-50 text-sm">
                <ArrowUpRight className="w-3.5 h-3.5" /> Apri su IG
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
